import type { MemberSpec } from './types';

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface MentionContext {
  /** The text typed after the `@`, used to filter suggestions. */
  query: string;
  /** Index of the `@` in the value, used to replace on selection. */
  start: number;
}

/**
 * Detect an in-progress `@mention` immediately before the caret.
 * Returns null when the caret isn't in a mention token.
 */
export function detectMention(value: string, caret: number): MentionContext | null {
  const before = value.slice(0, Math.max(0, caret));
  const m = before.match(/(?:^|\s)@([\w-]*)$/);
  return m ? { query: m[1], start: caret - m[1].length - 1 } : null;
}

/** Replace the active mention token with `@Name ` and return the new value. */
export function applyMention(value: string, mention: MentionContext, name: string, caret: number): string {
  return `${value.slice(0, mention.start)}@${name} ${value.slice(caret)}`;
}

/**
 * Insert an `@Name ` mention into the composer, appended with a leading space when needed.
 * Used when a user CLICKS an agent's name in a rendered message (not the typed-@ picker).
 */
export function appendMention(value: string, name: string): string {
  const base = value.replace(/\s+$/, '');
  const sep = base.length ? ' ' : '';
  return `${base}${sep}@${name} `;
}

/**
 * When an agent names another agent in a message (rendered as **bold**), we want that name to be a
 * clickable mention. Given the bold run's text, return the matching member name (canonical casing) or
 * null. Tolerates a trailing possessive/punctuation ("Voyage's", "Voyage:") and a leading `@`.
 */
export function matchMemberName(text: string, memberNames: string[]): string | null {
  const raw = text.trim().replace(/^@/, '');
  for (const name of memberNames) {
    // CASE-SENSITIVE on purpose: agent names are proper nouns (capitalised), and many read as ordinary
    // words ("Scout", "Frame", "Prism"). Matching case-insensitively would turn a bolded common word in
    // prose into a stray clickable mention. Allow a trailing possessive / punctuation boundary.
    if (new RegExp(`^${escapeRe(name)}(?:['’]s|[.,:;!?)]*)?$`).test(raw)) return name;
  }
  return null;
}

/**
 * Resolve `@Name` mentions in the text to member ids (de-duplicated).
 * Empty result means "address the whole group".
 */
export function resolveMentionTargets(text: string, members: MemberSpec[]): string[] {
  const ids = members
    .filter((m) => new RegExp(`@${escapeRe(m.name)}\\b`, 'i').test(text))
    .map((m) => m.id);
  return Array.from(new Set(ids));
}
